import type { Asset } from '../model/asset-types';
import { readImageFileNaturalSize } from '@/shared/lib/utils/image-utils';

/**
 * 获取素材的可下载或播放的原始 URL，若无原始 URL 则返回预览 URL，若均无则返回空字符串。
 */
export function getAssetUrl(asset: Asset): string {
  return asset.originalUrl || asset.previewUrl || '';
}

/**
 * 获取素材的显示名称
 */
export function getAssetDisplayName(asset: Asset): string {
  return asset.title || '未命名素材';
}

/**
 * 获取素材类型的中文描述
 */
export function getAssetTypeLabel(type: Asset['type']): string {
  const labels: Record<Asset['type'], string> = {
    image: '图片',
    video: '视频',
    audio: '音频'
  };
  return labels[type] || '未知类型';
}

/**
 * 根据 MIME 类型获取素材业务类型
 */
export function getAssetTypeFromMime(mime: string): Asset['type'] {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  return 'image'; // 默认兜底
}

/**
 * 获取图片文件的原始尺寸
 */
export async function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return readImageFileNaturalSize(file);
}

/**
 * 获取音频文件的时长（秒）
 */
export async function getAudioDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    audio.onloadedmetadata = () => {
      resolve(audio.duration);
      URL.revokeObjectURL(audio.src);
    };
    audio.onerror = () => reject(new Error('音频加载失败'));
    audio.src = URL.createObjectURL(file);
  });
}

/**
 * 获取视频文件的原始尺寸
 */
export async function getVideoDimensions(file: File): Promise<{ width: number; height: number; duration: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.onloadedmetadata = () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration
      });
      URL.revokeObjectURL(video.src);
    };
    video.onerror = () => reject(new Error('视频加载失败'));
    video.src = URL.createObjectURL(file);
  });
}
